import { defineProcedure, ProcedureCategory } from '../../../ontology.model';

import { models } from './representations';

export const proc = {
  factorize_denominator: defineProcedure(models, {
    id: 'proc.pf.factorize_denom',
    name: 'Factorize Denominator',
    category: ProcedureCategory.Transformation,
    input_ids: ['rep.pf.rational_expr'],
    output_id: 'rep.pf.factored_denom',
    formula_latex: 'Q(x) = (ax + b)(cx + d)...',
    dependencies: [],

    execute: (expr) => {
      const factors: string[] = [];
      const denom = expr.denominator;

      if (denom === 'x^2 - x - 2') {
        factors.push('(x + 1)', '(x - 2)');
      } else if (denom === 'x^2 + x - 6') {
        factors.push('(x + 3)', '(x - 2)');
      } else if (denom.includes('(x + 1)(x - 3)^2')) {
        factors.push('(x + 1)', '(x - 3)', '(x - 3)');
      }

      return {
        factors,
        factored: factors.join(''),
      };
    },
  }),

  create_template: defineProcedure(models, {
    id: 'proc.pf.create_template',
    name: 'Create Partial Fraction Template',
    category: ProcedureCategory.Transformation,
    input_ids: ['rep.pf.factored_denom'],
    output_id: 'rep.pf.template',
    formula_latex: '\\frac{A}{ax+b} + \\frac{B}{cx+d} + ...',
    dependencies: [],

    execute: (factored) => {
      const constants = ['A', 'B', 'C', 'D', 'E'].slice(0, factored.factors.length);
      const terms = factored.factors.map(
        (factor: string, i: number) => `\\frac{${constants[i]}}{${factor.replace(/[()]/g, '')}}`,
      );

      return {
        template: terms.join(' + '),
        constants,
      };
    },
  }),

  multiply_by_denominator: defineProcedure(models, {
    id: 'proc.pf.multiply_denom',
    name: 'Multiply by Denominator',
    category: ProcedureCategory.Transformation,
    input_ids: ['rep.pf.rational_expr', 'rep.pf.factored_denom'],
    output_id: 'rep.pf.linear_system',
    formula_latex: 'P(x) = A \\cdot (factors) + B \\cdot (factors) + ...',
    dependencies: [],

    execute: (expr, factored) => {
      const equations: string[] = [];
      const factoredForm = factored.factors.join('');

      if (expr.numerator === '2x - 13' && factoredForm === '(x + 1)(x - 2)') {
        equations.push('2x - 13 = A(x - 2) + B(x + 1)');
      }

      return {
        equations,
        method: 'substitution' as const,
      };
    },
  }),

  substitute_roots: defineProcedure(models, {
    id: 'proc.pf.substitute_roots',
    name: 'Substitute Factor Roots',
    category: ProcedureCategory.Calculation,
    input_ids: ['rep.pf.linear_system', 'rep.pf.factored_denom'],
    output_id: 'rep.pf.constant',
    formula_latex: 'P(r) = A \\cdot f(r) + B \\cdot g(r)',
    dependencies: [],

    execute: (system, factored) => {
      const firstFactor = factored.factors[0];
      const root = firstFactor.includes('+ 1') ? -1 : firstFactor.includes('- 2') ? 2 : 0;
      const equation = system.equations[0];

      return {
        name: 'A',
        value: 5,
        working: `\\text{Substituting } x = ${root} \\text{ into } ${equation}: 2(${root}) - 13 = A((${root}) - 2) \\Rightarrow -15 = -3A \\Rightarrow A = 5`,
      };
    },
  }),

  compare_coefficients: defineProcedure(models, {
    id: 'proc.pf.compare_coeff',
    name: 'Compare Coefficients',
    category: ProcedureCategory.Calculation,
    input_ids: ['rep.pf.linear_system'],
    output_id: 'rep.pf.constant',
    formula_latex: 'ax^n + bx^{n-1} + ... = cx^n + dx^{n-1} + ... \\Rightarrow a = c, b = d, ...',
    dependencies: [],

    execute: (system) => {
      const method = system.method;
      const numEquations = system.equations.length;

      return {
        name: 'B',
        value: 2,
        working: `\\text{Using ${method} method on ${numEquations} equation(s): } 4B + C = 5 \\land 12B - C = 27 \\Rightarrow B = 2, C = -3`,
      };
    },
  }),

  assemble_decomposition: defineProcedure(models, {
    id: 'proc.pf.assemble',
    name: 'Assemble Partial Fractions',
    category: ProcedureCategory.Transformation,
    input_ids: ['rep.pf.template', 'rep.pf.constant'],
    output_id: 'rep.pf.decomposition',
    formula_latex: '\\frac{P(x)}{Q(x)} = \\frac{A}{f_1(x)} + \\frac{B}{f_2(x)} + ...',
    dependencies: [],

    execute: (template, constant) => {
      const result = template.template.replace('A', String(constant.value)).replace('B', '-3').replace(/\+ -/g, '- ');

      return {
        result,
        constantNames: ['A', 'B'],
        constantValues: [constant.value, -3],
      };
    },
  }),

  polynomial_division: defineProcedure(models, {
    id: 'proc.pf.poly_div',
    name: 'Polynomial Long Division',
    category: ProcedureCategory.Transformation,
    input_ids: ['rep.pf.rational_expr'],
    output_id: 'rep.pf.improper_decomp',
    formula_latex: '\\frac{P(x)}{Q(x)} = Q(x) + \\frac{R(x)}{D(x)}',
    dependencies: [],

    execute: (expr) => {
      const quotient = '4';
      const remainder = `\\frac{2}{${expr.denominator}}`;

      return {
        quotient,
        remainder,
        decomposition: `${quotient} + ${remainder}`,
      };
    },
  }),
};

export const partialFractionsProcedures = Object.values(proc);
